import { CaretDownIcon } from "@App/Components/common/Icons";
import { ServiceRow } from "@App/Components/common/ServiceRow/ServiceRow";
import type { Service } from "@App/types";
import { getServiceIcon } from "@App/utils/getServiceIcon";
import type { ServiceGroup } from "@App/utils/groupServices";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import type { FunctionComponent } from "react";

export type ServiceRowGroupsProps = {
  groups: ServiceGroup[];
  onSelect: (service: Service) => void;
};

/**
 * The service list split into collapsible category sections.
 *
 * Every section starts closed, on both breakpoints: with a category per service area the page opens on
 * an overview of what is on offer, and opening one is a single tap. Expanding a section by default only
 * pays off when there are few enough categories to see the rest of them underneath it.
 *
 * On desktop the categories themselves sit two to a row, so ten of them fit in five rows rather than a
 * screen and a half of stacked headers; the services inside one then stack in a single column, because
 * a category is only half the width it used to be. `items-start` keeps an open category from stretching
 * its neighbour — the row grows, the header beside it does not.
 *
 * Widths match the rows exactly: one 300px column on mobile, two plus the 8px grid gap on desktop.
 */
export const ServiceRowGroups: FunctionComponent<ServiceRowGroupsProps> = ({
  groups,
  onSelect,
}: ServiceRowGroupsProps) => {
  return (
    <div
      data-testid="service-groups"
      className="grid grid-cols-1 md:grid-cols-2 items-start gap-2 mb-4 mt-4 w-full max-w-[300px] md:max-w-none md:w-[608px]"
    >
      {groups.map((group) => (
        <Disclosure key={group.label}>
          {({ open }) => (
            <div>
              <DisclosureButton
                data-testid="service-group-header"
                className={`flex justify-between items-center gap-3 w-full border-[0.5px] rounded py-2.5 px-3 text-left cursor-pointer focus:outline-none hover:border-primary ${
                  open ? "border-primary" : "border-neutral-300"
                }`}
              >
                <span className="font-serif text-sm">{group.label}</span>
                <span className="flex items-center gap-2 text-xs text-neutral-600">
                  {group.services.length}
                  <span
                    className={`flex w-4 h-4 items-center transition-transform duration-150 ${
                      open ? "rotate-180" : "rotate-0"
                    }`}
                  >
                    <CaretDownIcon />
                  </span>
                </span>
              </DisclosureButton>
              <DisclosurePanel
                data-testid="service-group-panel"
                className="grid grid-cols-1 gap-2 pt-2"
              >
                {group.services.map((service) => (
                  <ServiceRow
                    key={service.id}
                    title={service.pivot.display_name ? service.pivot.display_name : service.name}
                    subtitle={service.pivot.subtitle}
                    icon={getServiceIcon(service.pivot.icon)}
                    onClick={() => onSelect(service)}
                  />
                ))}
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>
      ))}
    </div>
  );
};
